import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { analyticsAPI } from "../services/api";
import { RefreshCw, FileSpreadsheet } from "lucide-react";

interface RoomLldStat {
  _id: string;
  name: string;
  lldRevenue: number;
}

// Bảng màu chấm tròn cạnh tên phòng — chỉ để dễ nhìn, không mang ý nghĩa gì khác
const ROOM_COLORS: string[] = [
  "#EF4444", "#3B82F6", "#C07850", "#10B981",
  "#F59E0B", "#EC4899", "#06B6D4", "#84CC16",
];

function formatCurrency(n: number): string {
  return new Intl.NumberFormat("vi-VN").format(n) + "đ";
}

function currentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

export default function LldRevenuePage(): React.ReactElement {
  const [month, setMonth] = useState<string>(currentMonth());
  const [rooms, setRooms] = useState<RoomLldStat[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await analyticsAPI.getRoomStats(month || undefined);
      setRooms(res.data.data || []);
    } catch {
      toast.error("Lỗi khi tải doanh thu LLD");
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Sắp theo doanh thu LLD giảm dần — dễ đối chiếu với sheet, phòng đóng góp
  // nhiều nhất lên đầu; ẩn hẳn phòng 0đ để danh sách gọn, đúng trọng tâm đối chiếu
  const sortedRooms = useMemo(
    () => rooms.filter((r) => r.lldRevenue > 0).sort((a, b) => b.lldRevenue - a.lldRevenue),
    [rooms]
  );

  const total = useMemo(() => sortedRooms.reduce((s, r) => s + r.lldRevenue, 0), [sortedRooms]);

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900 flex items-center gap-2">
            <FileSpreadsheet size={22} className="text-coffee-600" />
            Doanh thu LLD
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Doanh thu LLD từng phòng theo tháng — đối chiếu nhanh với sheet gốc
          </p>
        </div>

        <div className="flex items-center gap-2">
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="px-3 py-1.5 border border-zinc-200 rounded-lg text-sm focus:outline-none focus:border-coffee-400 bg-white text-zinc-700"
          />
          <button
            onClick={fetchData}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-zinc-200 rounded-lg text-sm text-zinc-600 hover:bg-zinc-50 transition-colors"
          >
            <RefreshCw size={14} />
            Tải lại
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-zinc-400">Đang tải...</div>
      ) : sortedRooms.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-zinc-400">
          <FileSpreadsheet size={40} className="mb-3 opacity-50" />
          Không có doanh thu LLD trong tháng này
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-zinc-200 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-200 bg-zinc-50">
                <th className="text-left px-4 py-3 font-semibold text-zinc-600">#</th>
                <th className="text-left px-4 py-3 font-semibold text-zinc-600">Phòng</th>
                <th className="text-right px-4 py-3 font-semibold text-zinc-600">Doanh thu LLD</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {sortedRooms.map((r, i) => (
                <tr key={r._id} className="hover:bg-zinc-50">
                  <td className="px-4 py-3 text-zinc-400">{i + 1}</td>
                  <td className="px-4 py-3 font-medium text-zinc-800">
                    <span className="inline-flex items-center gap-2">
                      <span
                        className="w-2.5 h-2.5 rounded-full shrink-0"
                        style={{ backgroundColor: ROOM_COLORS[i % ROOM_COLORS.length] }}
                      />
                      {r.name}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-coffee-700">
                    {formatCurrency(r.lldRevenue)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-zinc-200 bg-zinc-50">
                <td colSpan={2} className="px-4 py-3 font-bold text-zinc-800">Tổng cộng</td>
                <td className="px-4 py-3 text-right font-bold text-zinc-900">{formatCurrency(total)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
